import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, CalendarDays, Loader2, Plus, Trash2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import type { ApiResponse } from '@/types/api';
import { InstitutionStyledSelect } from '@/components/institution/InstitutionStyledSelect';
import { institutionListSearchShellClass } from '@/components/institution/InstitutionStyledSearchInput';
import { Switch } from '@/components/ui/Switch';

export type ReminderTargetKind = 'eduflow';

type RuleKind =
  | 'eduflow_not_started_days_after_enroll'
  | 'eduflow_incomplete_days_after_deadline'
  | 'path_incomplete_days_after_assign';

type RuleRow = {
  clientKey: string;
  ruleKind: RuleKind;
  daysAfter: number;
  sendHourUtc: number;
  enabled: boolean;
  label: string;
};

type LogRow = {
  id: string;
  ruleId: string;
  sentAt: string;
  recipientEmail: string;
  status: string;
  errorMessage: string | null;
  dedupeKey: string;
  titleSnapshot: string | null;
};

function newClientKey() {
  return `tmp-${Math.random().toString(36).slice(2, 11)}`;
}

const FLOW_RULE_KINDS: RuleKind[] = [
  'eduflow_not_started_days_after_enroll',
  'eduflow_incomplete_days_after_deadline',
];

export default function InstitutionTrainingRemindersSlideOver({
  targetKind,
  targetId,
  title,
  onClose,
}: {
  targetKind: ReminderTargetKind;
  targetId: string;
  title: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.resolvedLanguage ?? i18n.language ?? 'fr').replace('_', '-');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [logs, setLogs] = useState<LogRow[]>([]);

  const allowedKinds = useMemo(
    () => FLOW_RULE_KINDS,
    [targetKind],
  );

  const defaultKind = allowedKinds[0]!;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<
        ApiResponse<{
          rules: Array<{
            id: string;
            ruleKind: RuleKind;
            daysAfter: number;
            sendHourUtc: number;
            enabled: boolean;
            label: string | null;
          }>;
          logs: LogRow[];
        }>
      >(`/institution/training-reminders/${targetKind}/${targetId}`);
      const data = res.data;
      const r = (data?.rules ?? []).map((row) => ({
        clientKey: row.id,
        ruleKind: row.ruleKind,
        daysAfter: row.daysAfter,
        sendHourUtc: row.sendHourUtc,
        enabled: row.enabled !== false,
        label: typeof row.label === 'string' ? row.label : '',
      }));
      setRules(r);
      setLogs(data?.logs ?? []);
    } catch {
      setError(t('institution.reminders.loadError'));
      setRules([]);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [t, targetId, targetKind]);

  useEffect(() => {
    void load();
  }, [load]);

  const hourUtcOptions = useMemo(
    () => Array.from({ length: 24 }, (_, h) => ({ value: String(h), label: `${String(h).padStart(2, '0')}h UTC` })),
    [],
  );

  const ruleKindOptions = useMemo(
    () => allowedKinds.map((k) => ({ value: k, label: t(`institution.reminders.ruleKind.${k}`) })),
    [allowedKinds, t],
  );

  const addRule = () => {
    setRules((prev) => [
      ...prev,
      {
        clientKey: newClientKey(),
        ruleKind: defaultKind,
        daysAfter: 3,
        sendHourUtc: 8,
        enabled: true,
        label: '',
      },
    ]);
  };

  const removeRule = (key: string) => {
    setRules((prev) => prev.filter((r) => r.clientKey !== key));
  };

  const updateRule = (key: string, patch: Partial<RuleRow>) => {
    setRules((prev) => prev.map((r) => (r.clientKey === key ? { ...r, ...patch } : r)));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        rules: rules.map((r) => ({
          ruleKind: r.ruleKind,
          daysAfter: Number(r.daysAfter),
          sendHourUtc: Number(r.sendHourUtc),
          enabled: r.enabled,
          label: r.label.trim() ? r.label.trim() : null,
        })),
      };
      const res = await api.put<
        ApiResponse<{
          rules: Array<{
            id: string;
            ruleKind: RuleKind;
            daysAfter: number;
            sendHourUtc: number;
            enabled: boolean;
            label: string | null;
          }>;
          logs: LogRow[];
        }>
      >(`/institution/training-reminders/${targetKind}/${targetId}`, payload);
      const data = res.data;
      const r = (data?.rules ?? []).map((row) => ({
        clientKey: row.id,
        ruleKind: row.ruleKind,
        daysAfter: row.daysAfter,
        sendHourUtc: row.sendHourUtc,
        enabled: row.enabled !== false,
        label: typeof row.label === 'string' ? row.label : '',
      }));
      setRules(r);
      setLogs(data?.logs ?? []);
    } catch {
      setError(t('institution.reminders.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px]" onClick={onClose} aria-hidden />

      <div
        className="fixed end-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-s border-[#E8EDF5] bg-white shadow-2xl dark:border-[#19335E] dark:bg-[#0C1C38] animate-in duration-200 ltr:animate-slide-in-right rtl:animate-slide-in-left"
        role="dialog"
        aria-modal="true"
        aria-labelledby="institution-reminders-title"
      >
        <div className="flex items-center justify-between border-b border-[#F0F4FA] px-5 py-3 dark:border-[#122644]">
          <div className="flex min-w-0 items-center gap-2">
            <Bell className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
            <h2
              id="institution-reminders-title"
              className="truncate text-sm font-semibold uppercase tracking-wide text-[#5B6472] dark:text-[#B6CAEC]"
            >
              {t('institution.reminders.panelTitle')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-[#5B6472]/70 transition-colors hover:bg-[#F0F4FA] hover:text-[#5B6472] dark:hover:bg-[#122644] dark:hover:text-[#5B6472]/50"
            aria-label={t('institution.common.cancel')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b border-[#F0F4FA] px-5 py-3 dark:border-[#122644]">
          <p className="text-sm font-medium text-[#0C1C38] dark:text-white">{title}</p>
          <p className="mt-1 text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.reminders.panelSubtitle')}</p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-[#5B6472]">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}

              <section>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-[#5B6472] dark:text-[#B6CAEC]">
                    {t('institution.reminders.sectionRules')}
                  </h3>
                  <button
                    type="button"
                    onClick={addRule}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#E8EDF5] bg-white px-2 py-1 text-xs font-medium text-[#121826] hover:bg-[#F8FAFC] dark:border-[#B6CAEC] dark:bg-[#122644] dark:text-[#DCE7F7] dark:hover:bg-[#19335E]"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t('institution.reminders.addRule')}
                  </button>
                </div>
                <p className="mb-3 text-[11px] leading-snug text-[#5B6472] dark:text-[#B6CAEC]">
                  {t('institution.reminders.utcHint')}
                </p>
                {rules.length === 0 ? (
                  <p className="mb-3 text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.reminders.rulesEmpty')}</p>
                ) : null}
                <div className="space-y-3">
                  {rules.map((r) => (
                    <div
                      key={r.clientKey}
                      className="rounded-xl border border-[#E8EDF5] bg-[#F8FAFC]/80 p-3 dark:border-[#19335E] dark:bg-[#122644]/50"
                    >
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <InstitutionStyledSelect
                            panelZIndexClass="z-[160]"
                            label={t('institution.reminders.fieldKind')}
                            options={ruleKindOptions}
                            value={r.ruleKind}
                            onChange={(v) => updateRule(r.clientKey, { ruleKind: v as RuleKind })}
                          />
                        </div>
                        <button
                          type="button"
                          title={t('institution.reminders.removeRule')}
                          onClick={() => removeRule(r.clientKey)}
                          className="mt-7 shrink-0 rounded-lg p-1 text-[#5B6472]/70 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="min-w-0">
                          <label
                            htmlFor={`reminder-days-${r.clientKey}`}
                            className="mb-1.5 block text-xs font-medium text-[#121826] dark:text-[#DCE7F7]"
                          >
                            {t('institution.reminders.fieldDays')}
                          </label>
                          <div className={cn(institutionListSearchShellClass)}>
                            <span
                              className={cn(
                                'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border',
                                'border-primary-200 bg-primary-50 text-primary-600 dark:border-primary-800/60 dark:bg-primary-950/50 dark:text-primary-300',
                              )}
                              aria-hidden
                            >
                              <CalendarDays className="h-4 w-4 shrink-0 opacity-90" />
                            </span>
                            <input
                              id={`reminder-days-${r.clientKey}`}
                              type="number"
                              min={0}
                              max={3650}
                              value={r.daysAfter}
                              onChange={(e) => updateRule(r.clientKey, { daysAfter: Number(e.target.value) })}
                              className={cn(
                                'min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-medium leading-snug text-primary-900 shadow-none ring-0 outline-none',
                                'dark:text-primary-50',
                                '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                                'focus:ring-0 focus-visible:ring-0',
                              )}
                            />
                          </div>
                        </div>
                        <InstitutionStyledSelect
                          panelZIndexClass="z-[160]"
                          label={t('institution.reminders.fieldHourUtc')}
                          options={hourUtcOptions}
                          value={String(r.sendHourUtc)}
                          onChange={(v) => updateRule(r.clientKey, { sendHourUtc: Number(v) })}
                        />
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-[11px] text-[#5B6472] dark:text-[#DCE7F7]">
                        <Switch size="sm" checked={r.enabled} onChange={(checked) => updateRule(r.clientKey, { enabled: checked })} />
                        <span>{t('institution.reminders.fieldEnabled')}</span>
                      </div>
                      <label className="mt-2 block text-[11px] font-medium text-[#5B6472] dark:text-[#B6CAEC]">
                        {t('institution.reminders.fieldLabel')}
                        <input
                          type="text"
                          value={r.label}
                          onChange={(e) => updateRule(r.clientKey, { label: e.target.value })}
                          placeholder={t('institution.reminders.fieldLabelPh')}
                          className="mt-1 w-full rounded-lg border border-[#E8EDF5] bg-white px-2 py-1.5 text-xs dark:border-[#B6CAEC] dark:bg-[#0C1C38] dark:text-white"
                        />
                      </label>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void save()}
                  className={cn(
                    'mt-4 w-full rounded-lg bg-primary-900 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50',
                  )}
                  title={t('institution.reminders.save')}
                >
                  {saving ? (
                    <span className="inline-flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('institution.common.loading')}
                    </span>
                  ) : (
                    t('institution.reminders.save')
                  )}
                </button>
              </section>

              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#5B6472] dark:text-[#B6CAEC]">
                  {t('institution.reminders.sectionLogs')}
                </h3>
                {logs.length === 0 ? (
                  <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.reminders.logsEmpty')}</p>
                ) : (
                  <ul className="max-h-56 space-y-2 overflow-y-auto text-xs">
                    {logs.map((l) => (
                      <li
                        key={l.id}
                        className="rounded-lg border border-[#F0F4FA] bg-white px-2 py-2 dark:border-[#19335E] dark:bg-[#0C1C38]/60"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-1">
                          <span className="font-medium text-[#0C1C38] dark:text-white">{l.recipientEmail}</span>
                          <span
                            className={cn(
                              'rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase',
                              l.status === 'sent'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
                            )}
                          >
                            {l.status}
                          </span>
                        </div>
                        <p className="mt-0.5 text-[10px] text-[#5B6472] dark:text-[#B6CAEC]">
                          {new Date(l.sentAt).toLocaleString(dateLocale)}
                          {l.titleSnapshot ? ` · ${l.titleSnapshot}` : ''}
                        </p>
                        {l.errorMessage ? (
                          <p className="mt-1 text-[10px] text-red-600 dark:text-red-400">{l.errorMessage}</p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
